import React, { useEffect, useState } from 'react'

import { Field, Form, Formik, FormikHelpers } from 'formik'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { TagsInput } from 'react-tag-input-component'

import { ColorPickerInput } from '../common/ColorPickerInput'
import FormErrorMessage from '../common/FormErrorMessage'
import { fetchClients } from '../common/helpers'
import NiceButton from '../common/NiceButton'
import { RequiredMark } from '../common/RequiredMark'
import { ClientFormWrapper } from '../common/styledComponents'
import axiosClient from '../service/axiosClient'
import {
    CalendarEvent,
    CalendarEventFormData,
    CalendarEventType,
    EventFrequency,
    ReminderUnit
} from '../types/Calendar'
import ClientDTO from '../types/Client'

interface Props {
    id?: number | null
}

//5 min pred 10 min pred 15 min pred 30 min pred 1 hod pred atd
const getReminderOptions = (t: (k: string) => string) => [
    {reminder: '5', reminderUnit: ReminderUnit.MINUTES, text: t('reminder_5min')},
    {reminder: '15', reminderUnit: ReminderUnit.MINUTES, text: t('reminder_15min')},
    {reminder: '30', reminderUnit: ReminderUnit.MINUTES, text: t('reminder_30min')},
    {reminder: '1', reminderUnit: ReminderUnit.HOURS, text: t('reminder_1hour')},
    {reminder: '2', reminderUnit: ReminderUnit.HOURS, text: t('reminder_2hours')},
    {reminder: '5', reminderUnit: ReminderUnit.HOURS, text: t('reminder_5hours')},
    {reminder: '1', reminderUnit: ReminderUnit.DAYS, text: t('reminder_1day')},
    {reminder: '2', reminderUnit: ReminderUnit.DAYS, text: t('reminder_2days')},
    {reminder: '7', reminderUnit: ReminderUnit.DAYS, text: t('reminder_1week')},
]

const getFrequencyOptions = (t: (k: string) => string) => [
    {value: undefined, label: '-'},
    {value: EventFrequency.daily, label: t('freq_daily')},
    {value: EventFrequency.weekly, label: t('freq_weekly')},
    {value: EventFrequency.monthly, label: t('freq_monthly')},
]

export const CalendarEventForm = ({id}: Props) => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(!id)
    const [searchParams,] = useSearchParams()
    const queryClientId = searchParams.get('clientId')
    const entryDate = searchParams.get('entryDate')
    const navigate = useNavigate()
    const [clients, setClients] = useState<ClientDTO[]>([])
    const [eventTypes, setEventTypes] = useState<CalendarEventType[]>([])
    const [allDay, setAllDay] = useState<boolean>(false)
    const [event, setEvent] = useState<CalendarEventFormData>({
        description: '',
        clientId: queryClientId || '',
        eventType: 0,
        reminderUnit: '' as unknown as ReminderUnit,
        reminder: '',
        name: '',
        color: '',
        meetingLink: '',
        street: '',
        guestsEmailAddresses: [],
        city: '',
        zipCode: '',
        country: '',
        entryDate: entryDate ?? '',
        entryDateEnd: '',
        startedAt: '',
        endedAt: '',
        frequency: undefined,
        // publishFrom: queryStart ? new Date(queryStart) : null,
        // publishUntil: queryEnd ? new Date(queryEnd) : null,
    } as CalendarEventFormData)
    const {t} = useTranslation()
    const reminderOptions = getReminderOptions(t)
    const frequencyOptions = getFrequencyOptions(t)

    useEffect(() => {
        fetchClients().then(setClients).catch(console.error)
        axiosClient.get('calendar-event/type').then(response => {
            console.log('response types', response)
            setEventTypes(response.data)
        }).catch((e) => {
            console.log('e', e)
        })
    }, [])

    useEffect(() => {
        if (id) {
            axiosClient.get<CalendarEvent>('calendar-event/' + id)
                .then(response => {
                    console.log('response', response)
                    const postTmp = response.data
                    // Set allDay if no start/end times
                    setAllDay(!postTmp.startedAt && !postTmp.endedAt)
                    setEvent({
                        ...postTmp,
                        clientId: postTmp.clientId ? postTmp.clientId?.toString() : null,
                        eventType: postTmp.eventType?.id ?? 0,
                        guestsEmailAddresses: Array.isArray(postTmp.guestsEmailAddresses)
                            ? postTmp.guestsEmailAddresses
                            : (postTmp.guestsEmailAddresses ? postTmp.guestsEmailAddresses.split(',') : []),
                    } as CalendarEventFormData)
                    setIsDataLoaded(true)
                })
                .catch(error => {
                    console.error('Error loading calendar event:', error)
                    setIsDataLoaded(true)
                })
        }
    }, [id])

    const submit = async (values: CalendarEventFormData, actions: FormikHelpers<CalendarEventFormData>) => {
        console.log('values', values)

        try {
            // const response = await updateProfile({...values})
            setIsLoading(true)
            const request: Omit<CalendarEvent, 'id' | 'clientId' | 'lastName' | 'firstName' | 'reminderUnit' | 'eventType'> & {
                id?: number,
                clientId?: string,
                reminderUnit?: ReminderUnit
                eventType?: number
            } = {
                ...values,
                guestsEmailAddresses: values.guestsEmailAddresses.join(','),
                // Leaving the end date out means the event is on one day, which is the same
                // thing as an end date equal to the start
                entryDateEnd: values.entryDateEnd || values.entryDate,
            }

            if (!request.clientId) {
                delete request.clientId
            }
            if (!request.reminderUnit) {
                delete request.reminderUnit
            }
            if (!request.eventType) {
                delete request.eventType
            } else {
                request.eventType = +request.eventType
            }

            if (id) {
                request.id = id
            }

            const response = await axiosClient.post('calendar-event', request)

            if (id) {
                window.scrollTo(0, 0)
                navigate('/app/calendar')
                toast.success(t('event_updated_success'))
            } else if (response.data > 0) {
                toast.success(t('event_created_success'))
                navigate('/app/calendar')
            }
            // actions.resetForm()
        } catch (error: any) {
            toast.error(t('event_save_error'))
        } finally {
            setIsLoading(false)
        }
    }

    function eventTypesLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
        e.preventDefault()
        navigate('/app/edit-profile#calendar-event-types')
    }

    const validate = (values: Partial<CalendarEventFormData>) => {
        const errors: any = {}

        // if (!values?.clientId) {
        //     errors.clientId = t('required_field_error')
        // }
        if (!allDay && !values?.startedAt) {
            errors.startedAt = t('required_field_error')
        }
        if (!values?.entryDate) {
            errors.entryDate = t('required_field_error')
        }
        if (!values?.name?.trim()) {
            errors.name = t('required_field_error')
        }
        if (!values?.eventType) {
            errors.eventType = t('required_field_error')
        }

        // if (!values?.eventType) {
        //     errors.eventType = t('required_field_error')
        // }

        return errors
    }

    if (!isDataLoaded) {
        return null
    }

    return (
        <ClientFormWrapper>
            <div className="row d-flex">
                <div className="col-md-12">
                    <Formik
                        initialValues={event}
                        enableReinitialize
                        validate={validate}
                        onSubmit={submit}
                    >{({values, setFieldValue}) => (<>
                        <Form>
                            <div className="row d-flex">
                                <div className="form-group">
                                    <label htmlFor="name">{t('event_name')}<RequiredMark/></label>
                                    <Field
                                        id="name"
                                        name="name"
                                        className="form-control"/>
                                    <FormErrorMessage name="name"/>
                                </div>

                                <div className="col-md-5">
                                    <div
                                        className={'form-group'}>
                                        <label htmlFor="clientId">
                                            {t('client')}
                                        </label>
                                        <Field as="select" id="clientId" name="clientId" className="form-select">
                                            <option value="">{t('select')}</option>
                                            {clients.map((option) => (
                                                <option key={option.id}
                                                        value={option.id}>{option.companyName || (`${option.firstName} ${option.lastName}`)}</option>))}
                                        </Field>
                                        <FormErrorMessage name="clientId"/>
                                    </div>
                                </div>
                                <div className="col-md-5">
                                    <div
                                        className={'form-group'}>
                                        <label htmlFor="eventType">
                                            {t('event_type')}
                                        <RequiredMark/></label>&nbsp;<a href="#" onClick={eventTypesLinkClick}>({t('add_event_type')})</a>
                                        <select id="eventType" name="eventType"
                                                value={values.eventType}
                                                onChange={(e) => {
                                                    const event = eventTypes.find(et => et.id.toString() === e.target.value)
                                                    if (event?.color) {
                                                        setFieldValue('color', event?.color)
                                                    }
                                                    setFieldValue('eventType', e.target.value)
                                                }}
                                               className="form-select">
                                            <option value="">{t('select')}</option>
                                            {eventTypes.map((option) => (
                                                <option key={option.id}
                                                        value={option.id}>{option.name}</option>))}
                                        </select>
                                        <FormErrorMessage name="eventType"/>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label>{t('color')}</label>
                                        <ColorPickerInput
                                            color={values.color}
                                            setColor={(color) => setFieldValue('color', color)}/>
                                    </div>
                                </div>
                            </div>
                            <div className="row d-flex">
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label htmlFor="entryDate">{t('date')}<RequiredMark/></label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={values.entryDate}
                                            id="entryDate"
                                            name="entryDate"
                                            onChange={(e) => {
                                                setFieldValue('entryDate', e.target.value)
                                                // Always sync entryDateEnd when allDay is checked
                                                if (allDay) {
                                                    setFieldValue('entryDateEnd', e.target.value)
                                                }
                                                // Auto-sync entryDateEnd if it's empty or same as old entryDate
                                                else if (!values.entryDateEnd || values.entryDateEnd === values.entryDate) {
                                                    setFieldValue('entryDateEnd', e.target.value)
                                                }
                                                // If new entryDate is after entryDateEnd, update entryDateEnd
                                                else if (values.entryDateEnd && e.target.value > values.entryDateEnd) {
                                                    setFieldValue('entryDateEnd', e.target.value)
                                                }
                                            }}/>
                                        <FormErrorMessage name="entryDate"/>
                                    </div>

                                </div>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label htmlFor="startedAt">{t('start')}{!allDay && <RequiredMark/>}</label>
                                        <input
                                            className="form-control"
                                            type="time"
                                            value={values.startedAt}
                                            disabled={allDay}
                                            id="startedAt"
                                            name="startedAt"
                                            onChange={(e) => setFieldValue('startedAt', e.target.value)}/>
                                        <FormErrorMessage name="startedAt"/>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label htmlFor="endedAt">{t('end')}</label>
                                        <input
                                            className="form-control"
                                            type="time"
                                            value={values.endedAt}
                                            disabled={allDay}
                                            id="endedAt"
                                            name="endedAt"
                                            onChange={(e) => setFieldValue('endedAt', e.target.value)}/>
                                        <FormErrorMessage name="endedAt"/>
                                    </div>
                                </div>
                                <div className="col-md-1">
                                    <div className="form-group">
                                        <label htmlFor="allDay">{t('all_day')}</label>
                                        <div className="form-check mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={allDay}
                                                id="allDay"
                                                onChange={(e) => {
                                                    setAllDay(e.target.checked)
                                                    if (e.target.checked) {
                                                        setFieldValue('startedAt', '')
                                                        setFieldValue('endedAt', '')
                                                        setFieldValue('entryDateEnd', values.entryDate)
                                                    }
                                                }}/>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label htmlFor="frequency">{t('frequency')}</label>
                                        <select
                                            id="frequency"
                                            className="form-select"
                                            name={'frequency'}
                                            disabled={!!id}
                                            onChange={(e) => {
                                                const freq = frequencyOptions.find(option => option.value === e.target.value)
                                                console.log('freq?.value', freq?.value)
                                                setFieldValue('frequency', freq?.value || '')
                                                if (!freq?.value && values.entryDate) {
                                                    console.log('TERAZ')
                                                    setFieldValue('entryDateEnd', values.entryDate)
                                                }
                                            }}>
                                            <option value="">{t('select')}</option>
                                            {frequencyOptions.map((option) => (
                                                <option
                                                    key={option.value}
                                                    selected={option.value === values.frequency?.toString()}
                                                    value={option.value}>{option.label}</option>))}
                                        </select>
                                        <FormErrorMessage name="eventFrequency"/>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label htmlFor="entryDateEnd">{t('date_to')}</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={values.entryDateEnd}
                                            min={values.entryDate}
                                            disabled={allDay}
                                            id="entryDateEnd"
                                            name="entryDateEnd"
                                            onChange={(e) => {
                                                console.log('e.target.value', e.target.value)
                                                setFieldValue('entryDateEnd', e.target.value)
                                            }}/>
                                        <FormErrorMessage name="entryDateEnd"/>
                                    </div>

                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('invited_guests')}</label>
                                <TagsInput
                                    beforeAddValidate={(newTag, allTags) => {
                                        return !!(String(newTag)
                                            .toLowerCase()
                                            .match(
                                                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                                            ))
                                    }}
                                    value={values.guestsEmailAddresses}
                                    onChange={(tags) => setFieldValue('guestsEmailAddresses', tags)}
                                    name="guestsEmailAddresses"
                                    placeHolder={t('enter_email_press_enter')}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="description">{t('description')}</label>
                                <Field as="textarea" rows={5} id="description" name="description"
                                       className="form-control"/>
                                <FormErrorMessage name="description"/>
                            </div>

                            <div className="form-group">
                                <label htmlFor="meetingLink">{t('meeting_link')}</label>
                                <Field id="meetingLink" name="meetingLink"
                                       className="form-control"/>
                                <FormErrorMessage name="meetingLink"/>
                            </div>

                            <div className="row d-flex">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="street">{t('street')}</label>
                                        <Field id="street" name="street"
                                               className="form-control"/>
                                        <FormErrorMessage name="street"/>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="city">{t('city')}</label>
                                        <Field id="city" name="city"
                                               className="form-control"/>
                                        <FormErrorMessage name="city"/>
                                    </div>
                                </div>
                            </div>
                            <div className="row d-flex">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="zipCode">{t('zip')}</label>
                                        <Field id="zipCode" name="zipCode"
                                               className="form-control"/>
                                        <FormErrorMessage name="zipCode"/>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="country">{t('country')}</label>
                                        <Field id="country" name="country"
                                               className="form-control"/>
                                        <FormErrorMessage name="country"/>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={'form-group'}>
                                <label htmlFor="reminderu">
                                    {t('notify')}
                                </label>
                                <select
                                    id="reminderu"
                                    className="form-select"
                                    onChange={(e) => {
                                        const actualReminder = reminderOptions.find(option => option.text === e.target.value)
                                        setFieldValue('reminder', actualReminder?.reminder || '')
                                        setFieldValue('reminderUnit', actualReminder?.reminderUnit || '')
                                    }}>
                                    <option value="">{t('select')}</option>
                                    {reminderOptions.map((option) => (
                                        <option
                                            key={option.text}
                                            selected={option.reminderUnit === values.reminderUnit && option.reminder === values.reminder.toString()}
                                            value={option.text}>{option.text}</option>))}
                                </select>
                            </div>

                            {/*<div className="row d-flex">*/}
                            {/*    <div className="col-md-6">*/}
                            {/*        <div*/}
                            {/*            className={'form-group'}>*/}
                            {/*            <label htmlFor="reminder">*/}
                            {/*                Upozorniť*/}
                            {/*            </label>*/}
                            {/*            <Field id="reminder" name="reminder" className="form-control"/>*/}
                            {/*            <FormErrorMessage name="reminder"/>*/}
                            {/*        </div>*/}
                            {/*    </div>*/}

                            {/*    <div className="col-md-6">*/}
                            {/*        <div*/}
                            {/*            className={'form-group'}>*/}
                            {/*            <label htmlFor="reminderUnit">*/}
                            {/*                Jednotka upozornenia*/}
                            {/*            </label>*/}
                            {/*            <Field as="select" id="reminderUnit" name="reminderUnit"*/}
                            {/*                   className="form-control">*/}
                            {/*                <option value="">Vyberte</option>*/}
                            {/*                {reminderUnitOptions.map((option) => (*/}
                            {/*                    <option key={option.value}*/}
                            {/*                            value={option.value}>{option.label}</option>))}*/}
                            {/*            </Field>*/}
                            {/*            <FormErrorMessage name="reminderUnit"/>*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            <NiceButton isLoading={isLoading} type="submit">
                                {t('save')}
                            </NiceButton>
                        </Form>
                    </>)}

                    </Formik>
                </div>
            </div>
        </ClientFormWrapper>
    )
}